/**
 * turf_core.ts — THE ONE TURF COLOUR, pure (no three, no DOM): the rule the grass blades AND the ground's
 * turf layer both read (2026-09-25, visual-realism R2; resumed session — the cross-agent risk closed).
 *
 * WHY A FUNCTION OF THE TINT, NOT OF THE BIOME WEIGHTS. The ground's v2 vertex layout (lib/ground_layers.ts,
 * 20 interleaved floats) carries NO biome weights: its only climate channel is `gTint.rgb` — the biome-
 * weighted `BIOME_TINT` — beside the dryness (`gTint.w`) and the wet mask (`gMask.z`). A colour rule the
 * ground can evaluate per pixel therefore has to be a function of (tint, dryness, wetness), and so the grass
 * reads the SAME three numbers off the chunk's own vertex data (lib/grass/grass_driver.ts `groundAt`) and
 * calls the same function. One rule, one set of inputs: the blade over a meadow and the meadow beyond the
 * grass reach cannot drift apart.
 *
 * THE PALETTE LOOKUP. The curated per-biome turf palette (`TURF_SRGB`) is looked up from the tint by an
 * inverse-square-distance weighting over the six biome tints (Shepard interpolation): EXACT at every pure
 * biome (a biome's tint IS its palette entry), smooth between them. Measured 2026-09-25: an affine fit from
 * tint to palette misses by ~30 sRGB units (crystal teal came out (0, 106, 97)) and a tint power drops every
 * hue the palette carries (teal, mauve, straw) — the tints are near-grey multipliers — so neither can stand
 * in for the palette. The cost of Shepard is a small leak of a third biome's hue in the middle of a two-way
 * blend (plains ↔ wetland at 50 % reads a few units bluer); `verify:grass` bounds it on every blend that
 * occurs in the world.
 *
 * Then dryness pulls toward sun-bleached straw and wetness toward a deep, saturated green (as before).
 * Output: LINEAR RGB. `turf_color.ts` carries the TSL mirror (`turfColorAtNode`) the ground shader calls —
 * change the formula here and there together; `verify:grass` pins the constants and the shape.
 */
import { BIOME_TINT } from '../ground_layers.js';
import type { Biome } from '../../../../shared/terrain.js';

/** BIOMES order (the chunk attribute packing, `shared/terrain.ts BIOMES`). */
export const TURF_ORDER: readonly Biome[] = ['regolith_plains', 'crystal_forest', 'dune_desert', 'rocky_highlands', 'moss_wetland', 'fungal_basin'];

/** Turf palette per biome, sRGB bytes — the lush, midday colour of that biome's ground cover. */
export const TURF_SRGB: Record<Biome, readonly [number, number, number]> = {
  regolith_plains: [118, 126, 64],
  crystal_forest: [72, 124, 116],
  dune_desert: [166, 150, 102],
  rocky_highlands: [100, 108, 70],
  moss_wetland: [56, 106, 74],
  fungal_basin: [106, 88, 108],
};
/** Where dryness pulls every biome: sun-bleached straw. */
export const TURF_DRY_SRGB: readonly [number, number, number] = [178, 160, 108];
/** Where wetness pulls it: a deep, saturated green (the valley floor, the stream bank). */
export const TURF_WET_SRGB: readonly [number, number, number] = [44, 86, 52];
/** How far each pull reaches at 1 (a fully dry field is not pure straw). */
export const TURF_DRY_MAX = 0.72;
export const TURF_WET_MAX = 0.55;
/** The Shepard weight's floor: 1/(d² + ε). Tiny, so a pure biome's own entry outweighs the rest ~10⁶ : 1. */
export const TURF_SHEPARD_EPS = 1e-9;

/** sRGB byte → linear (the exact piecewise curve). */
export function srgbByteToLinear(b: number): number {
  const c = b / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
/** linear → sRGB byte (0..255, rounded). */
export function linearToSrgbByte(l: number): number {
  const c = l <= 0 ? 0 : l >= 1 ? 1 : l;
  const s = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.max(0, Math.min(255, Math.round(s * 255)));
}
const lin = (rgb: readonly [number, number, number]): [number, number, number] => [srgbByteToLinear(rgb[0]), srgbByteToLinear(rgb[1]), srgbByteToLinear(rgb[2])];
/** The palettes in LINEAR light, BIOMES order. */
export const TURF_LINEAR: ReadonlyArray<readonly [number, number, number]> = TURF_ORDER.map((b) => lin(TURF_SRGB[b]));
export const TURF_DRY_LINEAR = lin(TURF_DRY_SRGB);
export const TURF_WET_LINEAR = lin(TURF_WET_SRGB);
/** The ground's biome tints in BIOMES order — the lookup's anchors (lib/ground_layers.ts owns the values). */
export const TURF_TINTS: ReadonlyArray<readonly [number, number, number]> = TURF_ORDER.map((b) => BIOME_TINT[b]);

/** Any writable RGB triple (a plain array, or a typed array of either precision). */
export type TurfRgb = Float32Array | Float64Array | number[];

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v; }

/**
 * The ground's tint for six biome weights — EXACTLY lib/ground_rules.ts's `gTint.rgb` formula
 * (Σ w·BIOME_TINT / Σ w, weights taken as given); all-zero reads as the plains.
 */
export function turfTintOf(w: ArrayLike<number>, out: TurfRgb = new Float32Array(3)): TurfRgb {
  let s = 0, r = 0, g = 0, b = 0;
  for (let i = 0; i < 6; i++) { const k = w[i] ?? 0; s += k; r += TURF_TINTS[i][0] * k; g += TURF_TINTS[i][1] * k; b += TURF_TINTS[i][2] * k; }
  if (!(s > 1e-6)) { out[0] = TURF_TINTS[0][0]; out[1] = TURF_TINTS[0][1]; out[2] = TURF_TINTS[0][2]; return out; }
  out[0] = r / s; out[1] = g / s; out[2] = b / s;
  return out;
}

/**
 * THE ONE FUNCTION: the turf colour (LINEAR RGB) for the ground's tint (rgb), dryness and wetness (0..1).
 * The ground calls its TSL mirror per pixel with its vertex attributes; the grass calls this with the same
 * attributes read off the chunk mesh.
 */
export function turfColorAt(tr: number, tg: number, tb: number, dry: number, wet: number, out: TurfRgb = new Float32Array(3)): TurfRgb {
  let ws = 0, r = 0, g = 0, b = 0;
  for (let i = 0; i < 6; i++) {
    const t = TURF_TINTS[i];
    const dx = tr - t[0], dy = tg - t[1], dz = tb - t[2];
    const k = 1 / (dx * dx + dy * dy + dz * dz + TURF_SHEPARD_EPS);
    ws += k; r += TURF_LINEAR[i][0] * k; g += TURF_LINEAR[i][1] * k; b += TURF_LINEAR[i][2] * k;
  }
  r /= ws; g /= ws; b /= ws;
  const d = clamp01(dry) * TURF_DRY_MAX, q = clamp01(wet) * TURF_WET_MAX;
  r += (TURF_DRY_LINEAR[0] - r) * d; g += (TURF_DRY_LINEAR[1] - g) * d; b += (TURF_DRY_LINEAR[2] - b) * d;
  r += (TURF_WET_LINEAR[0] - r) * q; g += (TURF_WET_LINEAR[1] - g) * q; b += (TURF_WET_LINEAR[2] - b) * q;
  out[0] = r; out[1] = g; out[2] = b;
  return out;
}

const tintScratch = new Float32Array(3);
/** The turf colour for six biome weights (BIOMES order): the weights' tint, then `turfColorAt`. */
export function turfColor(w: ArrayLike<number>, dry: number, wet: number, out: TurfRgb = new Float32Array(3)): TurfRgb {
  turfTintOf(w, tintScratch);
  return turfColorAt(tintScratch[0], tintScratch[1], tintScratch[2], dry, wet, out);
}
